import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { 
  ArrowRight, 
  Target, 
  Heart, 
  Award, 
  Users, 
  Vote, 
  FileText, 
  TrendingUp, 
  Globe 
} from "lucide-react";


const About = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="section-padding bg-primary-light">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">
              About The Partnership Lab
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Evidence-informed leadership consultancy, built specifically for the 
              unique challenges and opportunities of UK general practice partnerships.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Our Mission
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Better partnerships for better patient care
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                GP partnerships are the backbone of primary care in the UK. When partnerships 
                function well—with aligned values, clear governance, and motivated partners—
                patients receive better care and partners lead more sustainable, fulfilling careers.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                The Partnership Lab exists to help practices optimise their partnerships 
                <strong> before</strong> problems arise, and to support those under strain with 
                structured, evidence-informed tools.
              </p>
            </div>
            <div className="card-premium bg-primary-light border-none">
              <Target className="h-12 w-12 text-primary mb-6" />
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                Prevention over intervention
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                We believe the best partnership work happens before crisis, conflict, or breakdown. 
                Our focus is on optimisation, fairness, and sustained high performance—
                not just firefighting when things go wrong.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-secondary/30">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Our Values"
            title="What we stand for"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="card-premium text-center">
              <Award className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Evidence-Informed</h3>
              <p className="text-sm text-muted-foreground">
                Our tools are grounded in leadership research, adapted for the primary care context.
              </p>
            </div>
            <div className="card-premium text-center">
              <Users className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Partnership-Focused</h3>
              <p className="text-sm text-muted-foreground">
                Built specifically for GP partnerships, not generic business teams.
              </p>
            </div>
            <div className="card-premium text-center">
              <Target className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Discreet</h3>
              <p className="text-sm text-muted-foreground">
                Complete confidentiality. Individual responses are never shared.
              </p>
            </div>
            <div className="card-premium text-center">
              <Heart className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Purpose-Driven</h3>
              <p className="text-sm text-muted-foreground">
                Surplus profits donated to charity. Commercial success in service of social good.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* What We're Not */}
      <section className="section-padding bg-background">
        <div className="container-narrow">
          <SectionHeader
            eyebrow="Clarity"
            title="What we're not"
            description="Transparency about our scope helps you find the right support."
          />

          <div className="space-y-4">
            <div className="card-premium">
              <h3 className="font-semibold text-foreground mb-2">Not legal advisors</h3>
              <p className="text-muted-foreground">
                We provide leadership consultancy and facilitation, not legal advice. 
                If you need legal support with partnership agreements or disputes, 
                we can recommend appropriate specialists.
              </p>
            </div>
            <div className="card-premium">
              <h3 className="font-semibold text-foreground mb-2">Not formal mediators</h3>
              <p className="text-muted-foreground">
                Our facilitation is structured and professional, but we're not a formal 
                mediation service. For disputes requiring formal mediation, we'll point 
                you in the right direction.
              </p>
            </div>
            <div className="card-premium">
              <h3 className="font-semibold text-foreground mb-2">Not crisis-only</h3>
              <p className="text-muted-foreground">
                While we can support practices under strain, we're not positioned as an 
                emergency service. Our best work happens when practices invest in 
                partnership health proactively.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Impact Section */}
      <section id="social-impact" className="section-padding bg-primary text-primary-foreground">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Heart className="h-16 w-16 mx-auto mb-6 opacity-90" />
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Purpose Beyond Profit
            </h2>
            <p className="text-lg opacity-90 leading-relaxed">
              The Partnership Lab is built on a simple principle: commercial success 
              in service of social good. After fair professional fees and operating costs, 
              all surplus profits are donated to charity.
            </p>
          </div>
        </div>
      </section>

      {/* How Surplus Profits Support Charity */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Our Model"
            title="How surplus profits support charity"
            description="A transparent approach to purpose-driven business."
          />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Fair Professional Fees
              </h3>
              <p className="text-sm text-muted-foreground">
                We charge market-appropriate rates that reflect the value of 
                evidence-informed consultancy and expert facilitation.
              </p>
            </div>
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <Globe className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Sustainable Operations
              </h3>
              <p className="text-sm text-muted-foreground">
                Operating costs cover technology, development, and the infrastructure 
                needed to serve practices effectively.
              </p>
            </div>
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <Heart className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Surplus Donated
              </h3>
              <p className="text-sm text-muted-foreground">
                What remains after fair fees and costs goes to charity—
                not a token percentage, all surplus profits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Annual Charity Rotation */}
      <section className="section-padding bg-secondary/30">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Annual Charity Rotation
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
                You help choose where donations go
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                The recipient charity rotates annually. At the end of each year, 
                participating practices and individual users can vote on the 
                following year's charity partner.
              </p>
              <ul className="space-y-3">
                {[
                  "Annual voting process for all participants",
                  "Focus on charities aligned with health and wellbeing",
                  "Transparent selection criteria",
                  "Previous recipients remain part of the community",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Vote className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-premium bg-primary-light border-none">
              <Users className="h-12 w-12 text-primary mb-6" />
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                Community choice
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                By completing an Inventory or engaging our services, you become part of 
                a community with a voice in where surplus profits are directed. 
                One practice, one vote. One individual, one vote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="section-padding bg-background">
        <div className="container-narrow">
          <SectionHeader
            eyebrow="Transparency"
            title="Annual impact reporting"
            description="We believe in complete transparency about our charitable model."
          />

          <div className="card-premium">
            <div className="flex items-start gap-4">
              <FileText className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  What we publish annually
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Total revenue generated</li>
                  <li>• Professional fees and operating costs breakdown</li>
                  <li>• Surplus profits calculation</li>
                  <li>• Donation amount and recipient charity</li>
                  <li>• Impact achieved with the donation</li>
                  <li>• Next year's charity shortlist and voting results</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-12 md:py-16 bg-primary-light">
        <div className="container-narrow text-center">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">
            Why this matters
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            GP partnerships serve communities. When you invest in partnership health, 
            you're investing in better patient care. Through our surplus donation model, 
            that investment extends even further—supporting charities that make a 
            difference beyond the consulting room.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-narrow text-center text-primary-foreground">
          <h2 className="font-display text-3xl font-semibold mb-4">
            Let's talk about your partnership
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
            Book a free discovery call to discuss your practice's needs and 
            explore how we might help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="xl">
                Book a Discovery Call
              </Button>
            </Link>
            <Link to="/start-inventory">
              <Button variant="outline" size="xl" className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Start the Inventory
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
